/**
 * @group Utils
 * 
 * @summary
 * <blockquote>Builds a complete URL with base URL and query parameters. Handles absolute URLs correctly.</blockquote>
 * 
 * @example
 * ```typescript
 * // Relative URL - appends to baseURL
 * const url1 = buildUrl('https://api.example.com', '/users', { page: 1, limit: 10 });
 * // Returns: 'https://api.example.com/users?page=1&limit=10'
 * 
 * // Absolute URL - uses as-is, ignores baseURL
 * const url2 = buildUrl('https://local.com', 'https://external.com/api', { key: 'value' });
 * // Returns: 'https://external.com/api?key=value'
 * ```
 */
type QueryParams = Record<string, string | number | boolean>;

const appendQueryParams = (targetUrl: string, params?: QueryParams): string => {
  if (!params || Object.keys(params).length === 0) {
    return targetUrl;
  }

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    searchParams.append(key, String(value));
  });

  const queryString = searchParams.toString();
  if (!queryString) {
    return targetUrl;
  }

  const hashIndex = targetUrl.indexOf('#');
  const hasHashFragment = hashIndex !== -1;
  const urlWithoutHash = hasHashFragment ? targetUrl.slice(0, hashIndex) : targetUrl;
  const hashFragment = hasHashFragment ? targetUrl.slice(hashIndex) : '';

  const hasExistingQuery = urlWithoutHash.includes('?');
  const needsAmpersand = hasExistingQuery && !urlWithoutHash.endsWith('?') && !urlWithoutHash.endsWith('&');
  const delimiter = hasExistingQuery ? (needsAmpersand ? '&' : '') : '?';

  return `${urlWithoutHash}${delimiter}${queryString}${hashFragment}`;
};

const normalizeBaseUrl = (value: string): string => {
  if (!value) {
    return '';
  }

  return value.endsWith('/') ? value.slice(0, -1) : value;
};

const normalizeRelativeUrl = (value?: string): string => {
  if (!value) {
    return '';
  }

  if (value === '') {
    return '';
  }

  return value.startsWith('/') ? value : `/${value}`;
};

const isAbsoluteUrl = (value?: string): value is string => {
  if (!value) {
    return false;
  }

  return value.startsWith('http://') || value.startsWith('https://');
};

/**
 * @internal
 */
export function buildUrl(
  baseURL: string,
  url?: string,
  params?: QueryParams
): string {
  if (isAbsoluteUrl(url)) {
    return appendQueryParams(url, params);
  }

  const normalizedBaseUrl = normalizeBaseUrl(baseURL);
  const normalizedUrl = normalizeRelativeUrl(url);
  const fullUrl = normalizedUrl ? `${normalizedBaseUrl}${normalizedUrl}` : normalizedBaseUrl;

  return appendQueryParams(fullUrl, params);
}
